<template>
  <transition name="search-fade">
    <div v-if="open" class="search-overlay" @click.self="$emit('close')">
      <div class="search-modal">

        <div class="search-input-wrap">
          <v-icon size="20" color="#94A3B8" class="mr-3">mdi-magnify</v-icon>
          <input
            ref="searchInput"
            v-model="query"
            class="search-input"
            placeholder="Pretraži cijelu bazu artikala..."
            @input="onInput"
            @keydown.down.prevent="moveDown"
            @keydown.up.prevent="moveUp"
            @keydown.enter.prevent="selectActive"
            @keydown.esc="$emit('close')"
          />
          <span class="search-kbd-esc">ESC</span>
        </div>

        <div class="search-results">
          <div v-if="loading" class="search-status">
            <v-progress-circular indeterminate size="20" width="2" color="#00BCD4" />
            <span>Pretražujem...</span>
          </div>
          <div v-else-if="query.length > 0 && query.length < 3" class="search-status">
            <v-icon size="28" color="#E0E6ED">mdi-keyboard-outline</v-icon>
            <span>Unesite još {{ 3 - query.length }} {{ 3 - query.length === 1 ? 'znak' : 'znaka' }}...</span>
          </div>
          <div v-else-if="query.length >= 3 && !results.length && !loading" class="search-status">
            <v-icon size="28" color="#E0E6ED">mdi-magnify-close</v-icon>
            <span>Nema rezultata za "{{ query }}"</span>
          </div>
          <div v-else-if="!query" class="search-status">
            <v-icon size="28" color="#E0E6ED">mdi-text-search</v-icon>
            <span>Unesite naziv artikla za pretragu</span>
          </div>
          <template v-else>
            <div
              v-for="(item, idx) in results"
              :key="item.id"
              class="search-row"
              :class="{ 'search-row--active': idx === activeIdx }"
              :ref="'row_' + idx"
              @mouseenter="activeIdx = idx"
              @click="selectItem(item)"
            >
              <div class="search-row-left">
                <div class="search-row-name">{{ item.name }}</div>
                <div v-if="item.code" class="search-row-sub">
                  <span>{{ item.code }}</span>
                </div>
              </div>
              <div class="search-row-right">
                <div class="search-row-prices">
                  <span class="search-row-price">{{ displayPrice(item) }}</span>
                  <span v-if="wholesaleView" class="search-row-mpc">{{ mpcLabel(item) }}</span>
                </div>
                <v-icon size="13" color="#00BCD4" class="ml-2">mdi-cart-plus</v-icon>
              </div>
            </div>
          </template>
        </div>

        <div class="search-footer">
          <span><kbd>↑↓</kbd> Navigacija</span>
          <span><kbd>Enter</kbd> Dodaj u košaricu</span>
          <span><kbd>Esc</kbd> Zatvori</span>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
import { collection, query, getDocs, where, limit } from 'firebase/firestore'
import { df } from '@/plugins/firebase'
import state from '@/state'
import triGram from '@/mixins/trigram'

export default {
  name: 'ShopSearchModal',
  mixins: [triGram],

  props: {
    open: { type: Boolean, default: false },
    wholesaleView: { type: Boolean, default: false }
  },

  data () {
    return {
      query: '',
      results: [],
      loading: false,
      activeIdx: 0,
      _debounce: null
    }
  },

  computed: {
    currency () {
      return state.getCurrentCompany()?.currency || 'EUR'
    }
  },

  watch: {
    open (val) {
      if (val) {
        this.query = ''
        this.results = []
        this.activeIdx = 0
        this.$nextTick(() => this.$refs.searchInput?.focus())
      }
    }
  },

  methods: {
    formatPrice (cents) {
      if (!cents) return '0,00 €'
      return (cents / 100).toFixed(2).replace('.', ',') + ' €'
    },
    displayPrice (item) {
      const mpc = item.price || 0
      if (!this.wholesaleView) return this.formatPrice(mpc)
      const taxes = item.prices?.[this.currency]?.taxes
      const rateBp = (taxes && taxes.length) ? taxes[0].rate : 2500
      const vpc = Math.round(mpc / (1 + rateBp / 10000))
      return this.formatPrice(vpc)
    },
    mpcLabel (item) {
      if (!this.wholesaleView) return null
      return this.formatPrice(item.price || 0)
    },

    onInput () {
      clearTimeout(this._debounce)
      this.activeIdx = 0
      if (this.query.length < 3) { this.results = []; return }
      this._debounce = setTimeout(() => this.search(), 400)
    },

    async search () {
      this.loading = true
      try {
        const tg = this.triGram(this.query)
        const constraints = Object.keys(tg).map(k => where(`meta.${k}`, '==', true))
        constraints.push(where('status', '==', 'OK'), where('active', '==', true), limit(20))

        const posId = state.getPointOfSale().id
        const q = query(collection(df, `location_pricelist/${posId}/items`), ...constraints)
        const snap = await getDocs(q)

        const cur = this.currency
        const items = []
        snap.forEach(doc => {
          const p = doc.data()
          if (p.warehouse_units?.units) {
            Object.keys(p.warehouse_units.units).forEach(key => {
              const u = p.warehouse_units.units[key]
              if (u.prices?.[cur]?.active) {
                items.push({
                  id: `${p.id}.${key}`,
                  name: u.name || p.name,
                  price: u.prices[cur].price || 0,
                  prices: u.prices,
                  selling_unit: u.storage_unit || key,
                  unit: u.storage_unit || key,
                  code: p.code || '',
                  type: 'product',
                  kpdcode: p.kpdcode || '',
                  naknade: p.naknade || undefined,
                  manual_price_input: p.manual_price_input || false
                })
              }
            })
          } else {
            items.push({
              id: p.id,
              name: p.name,
              price: p.prices?.[cur]?.price || 0,
              prices: p.prices || {},
              selling_unit: p.selling_unit || 'kom',
              unit: p.selling_unit || 'kom',
              code: p.code || '',
              type: 'product',
              kpdcode: p.kpdcode || '',
              naknade: p.naknade || undefined,
              manual_price_input: p.manual_price_input || false
            })
          }
        })
        this.results = items
      } catch (e) {
        console.error('ShopSearchModal:', e)
        this.results = []
      } finally {
        this.loading = false
      }
    },

    moveDown () {
      if (this.activeIdx < this.results.length - 1) {
        this.activeIdx++
        this.$nextTick(() => this.scrollToActive())
      }
    },
    moveUp () {
      if (this.activeIdx > 0) {
        this.activeIdx--
        this.$nextTick(() => this.scrollToActive())
      }
    },
    scrollToActive () {
      const refKey = 'row_' + this.activeIdx
      const el = this.$refs[refKey]
      const row = Array.isArray(el) ? el[0] : el
      if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    },
    selectActive () { if (this.results[this.activeIdx]) this.selectItem(this.results[this.activeIdx]) },
    selectItem (item) {
      this.$emit('add-item', item)
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.search-overlay {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(15,23,42,0.45);
  backdrop-filter: blur(4px);
  display: flex; align-items: flex-start; justify-content: center;
  padding-top: 80px;
}
.search-modal {
  width: 100%; max-width: 680px;
  background: #ffffff; border-radius: 16px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.18);
  overflow: hidden; display: flex; flex-direction: column;
  max-height: calc(100vh - 160px);
}
.search-input-wrap {
  display: flex; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #F1F5F9; flex-shrink: 0;
}
.search-input {
  flex: 1; border: none; outline: none;
  font-size: 16px; color: #1e293b; background: transparent;
}
.search-input::placeholder { color: #94A3B8; }
.search-kbd-esc { font-size: 10px !important; color: #64748B !important; background: #F1F5F9 !important; border: none !important; border-radius: 5px !important; padding: 2px 6px !important; box-shadow: none !important; font-family: inherit !important; }
.search-results { flex: 1; overflow-y: auto; min-height: 100px; max-height: 360px; scrollbar-width: thin; scrollbar-color: rgba(0,188,212,0.4) transparent; }
.search-results::-webkit-scrollbar { width: 4px; }
.search-results::-webkit-scrollbar-thumb { background: rgba(0,188,212,0.4); border-radius: 2px; }
.search-status { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 36px 20px; font-size: 13px; color: #94A3B8; }
.search-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; margin: 2px 8px; cursor: pointer; transition: background 0.1s; border-bottom: 1px solid #F8FAFC; border-radius: 8px; }
.search-row:hover, .search-row--active { background: rgba(0,188,212,0.07); border-bottom-color: transparent; }
.search-row-left { flex: 1; overflow: hidden; margin-right: 16px; }
.search-row-name { font-size: 14px; font-weight: 600; color: #1e293b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.search-row-sub { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #94A3B8; margin-top: 2px; font-weight: 400; }
.search-row-dot { color: #CBD5E1; }
.search-row-right { display: flex; align-items: center; flex-shrink: 0; }
.search-row-prices { display: flex; flex-direction: column; align-items: flex-end; }
.search-row-price { font-size: 14px; font-weight: 700; color: #00BCD4; line-height: 1.2; }
.search-row-mpc { font-size: 10px; color: #94A3B8; line-height: 1.2; }
.search-footer { display: flex; align-items: center; gap: 18px; padding: 10px 20px; background: #F8FAFC; border-top: 1px solid #F1F5F9; flex-shrink: 0; font-size: 11px; color: #B0BEC5; }
kbd { background: #F1F5F9 !important; border: none !important; border-radius: 5px !important; padding: 2px 6px !important; font-size: 10px !important; color: #64748B !important; font-family: inherit !important; box-shadow: none !important; }
.search-fade-enter-active, .search-fade-leave-active { transition: opacity 0.18s ease; }
.search-fade-enter, .search-fade-leave-to { opacity: 0; }
</style>
